import { defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

export const messages: Record<string, MessageDescriptor> = defineMessages({
	insertTitle: {
		id: 'fabric.editor.agentManagedExtension.insert.title.non-final',
		defaultMessage: 'AI block',
		description:
			'Quick insert / block menu title for inserting a Confluence block whose body is managed by a Rovo agent.',
	},
	insertDescription: {
		id: 'fabric.editor.agentManagedExtension.insert.description.non-final',
		defaultMessage: 'Create content that Rovo keeps updated',
		description: 'Quick insert / block menu description for inserting an AI Block.',
	},
	insertExecutiveSummaryTitle: {
		id: 'fabric.editor.agentManagedExtension.insert.executiveSummary.title.ai-non-final',
		defaultMessage: 'Executive summary [AI block]',
		description:
			'Quick insert title for inserting an AI Block preconfigured to create an executive summary.',
	},
	insertExecutiveSummaryDescription: {
		id: 'fabric.editor.agentManagedExtension.insert.executiveSummary.description.ai-non-final',
		defaultMessage: 'Summarize this page and its linked sources',
		description:
			'Quick insert description for an Executive Summary AI Block template that starts with current page sources and panel output.',
	},
	insertMeetingRecapTitle: {
		id: 'fabric.editor.agentManagedExtension.insert.meetingRecap.title.ai-non-final',
		defaultMessage: 'Meeting recap [AI block]',
		description:
			'Quick insert title for inserting an AI Block preconfigured to create a meeting recap.',
	},
	insertMeetingRecapDescription: {
		id: 'fabric.editor.agentManagedExtension.insert.meetingRecap.description.ai-non-final',
		defaultMessage: 'Recap meeting outcomes and action items',
		description:
			'Quick insert description for a Meeting Recap AI Block template that starts with current page sources.',
	},
	insertDecisionRegisterTitle: {
		id: 'fabric.editor.agentManagedExtension.insert.decisionRegister.title.ai-non-final',
		defaultMessage: 'Decision register [AI block]',
		description:
			'Quick insert title for inserting an AI Block preconfigured to create a decision register.',
	},
	insertDecisionRegisterDescription: {
		id: 'fabric.editor.agentManagedExtension.insert.decisionRegister.description.ai-non-final',
		defaultMessage: 'Track decisions in a Rovo-updated table',
		description:
			'Quick insert description for a Decision Register AI Block template that asks users to add source links before saving.',
	},
	customize: {
		id: 'fabric.editor.agentManagedExtension.toolbar.customize.non-final',
		defaultMessage: 'Customize',
		description:
			'Floating toolbar button label that opens the inline configuration for an AI Block.',
	},
	autoUpdateInterval: {
		id: 'fabric.editor.agentManagedExtension.toolbar.autoUpdateInterval.ai-non-final',
		defaultMessage: 'Auto-update interval',
		description:
			'Floating toolbar button label that opens update interval options for an AI Block.',
	},
	noAutoUpdate: {
		id: 'fabric.editor.agentManagedExtension.toolbar.noAutoUpdate.ai-non-final',
		defaultMessage: 'No auto-update',
		description:
			'Dropdown option label for removing the preferred auto-update interval from an AI Block.',
	},
	regenerate: {
		id: 'fabric.editor.agentManagedExtension.toolbar.regenerate.non-final',
		defaultMessage: 'Regenerate',
		description:
			'Floating toolbar button label that re-executes the saved configuration for an AI Block.',
	},
	owner: {
		id: 'fabric.editor.agentManagedExtension.config.owner.non-final',
		defaultMessage: 'Owner',
		description: 'Label for the owner of an AI Block in the configuration panel.',
	},
	lastSuccessfulExecution: {
		id: 'fabric.editor.agentManagedExtension.config.lastSuccessfulExecution.non-final',
		defaultMessage: 'Updated',
		description:
			'Label for the most recent successful execution time of an AI Block in the configuration panel.',
	},
	loadingBlockContents: {
		id: 'fabric.editor.agentManagedExtension.loadingBlockContents.non-final',
		defaultMessage: 'Executing block contents',
		description:
			'Accessible status label announced while an AI Block with no body content is executing.',
	},
	loadingConfiguration: {
		id: 'fabric.editor.agentManagedExtension.loadingConfiguration.ai-non-final',
		defaultMessage: 'Loading block configuration',
		description:
			'Accessible status label announced while an existing AI Block configuration is loading.',
	},
	failedToLoadConfiguration: {
		id: 'fabric.editor.agentManagedExtension.failedToLoadConfiguration.ai-non-final',
		defaultMessage: 'Failed to load configuration',
		description: 'Brief error message shown when an existing AI Block configuration fails to load.',
	},
	retryConfigurationLoad: {
		id: 'fabric.editor.agentManagedExtension.retryConfigurationLoad.ai-non-final',
		defaultMessage: 'Retry',
		description: 'Button label for retrying a failed AI Block configuration load.',
	},
	blockContentsFetchError: {
		id: 'fabric.editor.agentManagedExtension.blockContentsFetchError.non-final',
		defaultMessage: "There was an error generating the block's content",
		description: 'Error message shown when executing an AI Block with no body content fails.',
	},
	retryBlockExecution: {
		id: 'fabric.editor.agentManagedExtension.retryBlockExecution.non-final',
		defaultMessage: 'Retry',
		description: 'Button label that retries execution for an AI Block after execution fails.',
	},
	configureBlock: {
		id: 'fabric.editor.agentManagedExtension.config.title.non-final',
		defaultMessage: 'Customize AI block',
		description: 'Heading of the inline configuration panel for an AI Block.',
	},
	customInstructionsLabel: {
		id: 'fabric.editor.agentManagedExtension.config.customInstructionsLabel.non-final',
		defaultMessage: 'Custom instructions',
		description: 'Label for the textarea where the user enters the agent prompt for an AI Block.',
	},
	customInstructionsPlaceholder: {
		id: 'fabric.editor.agentManagedExtension.config.customInstructionsPlaceholder.non-final',
		defaultMessage:
			'Tell the agent what this block should contain. For example: "Summarise the latest takeaways from my meetings with Team A and write a short summary that..."',
		description: 'Placeholder text for the agent prompt textarea.',
	},
	save: {
		id: 'fabric.editor.agentManagedExtension.config.save.non-final',
		defaultMessage: 'Save',
		description: 'Save button in the inline configuration panel for an AI Block.',
	},
	cancel: {
		id: 'fabric.editor.agentManagedExtension.config.cancel.non-final',
		defaultMessage: 'Cancel',
		description: 'Cancel button in the inline configuration panel for an AI Block.',
	},
	blockLabel: {
		id: 'fabric.editor.agentManagedExtension.blockLabel.non-final',
		defaultMessage: 'AI block',
		description: 'Small lozenge label shown on the AI Block in the editor.',
	},
	fetchError: {
		id: 'fabric.editor.agentManagedExtension.fetchError.non-final',
		defaultMessage:
			'Unable to load this AI block. It may have been deleted or you may not have permission to view it.',
		description:
			'Error message shown in the block body when the AI-managed object cannot be loaded from the backend.',
	},
	saveError: {
		id: 'fabric.editor.agentManagedExtension.config.saveError.non-final',
		defaultMessage: 'There was an error saving the block configuration',
		description:
			'Inline error message shown in the configuration popup when saving or updating the AIMO fails.',
	},
	invalidDesiredUpdateFrequency: {
		id: 'fabric.editor.agentManagedExtension.config.invalidDesiredUpdateFrequency.ai-non-final',
		defaultMessage: 'Choose how often this block should update.',
		description:
			'Validation message shown when the preferred update interval is missing or invalid for an AI Block.',
	},
	missingOutputFormat: {
		id: 'fabric.editor.agentManagedExtension.config.missingOutputFormat.ai-non-final',
		defaultMessage: 'Choose an output format.',
		description:
			'Validation message shown when no output format has been selected for an AI Block.',
	},
	missingSources: {
		id: 'fabric.editor.agentManagedExtension.config.missingSources.ai-non-final',
		defaultMessage: 'Add at least one source.',
		description: 'Validation message shown when an AI Block configuration has no sources.',
	},
	emptyPrompt: {
		id: 'fabric.editor.agentManagedExtension.config.emptyPrompt.ai-non-final',
		defaultMessage: 'Custom instructions cannot be empty.',
		description: 'Validation message shown when manual prompt mode is saved with an empty prompt.',
	},
	missingGoal: {
		id: 'fabric.editor.agentManagedExtension.config.missingGoal.ai-non-final',
		defaultMessage: 'Choose what this block should create.',
		description: 'Validation message shown when no content goal has been selected for an AI Block.',
	},
	configurationAriaLabel: {
		id: 'fabric.editor.agentManagedExtension.config.ariaLabel.non-final',
		defaultMessage: 'AI block configuration',
		description:
			'Accessible label for the popup that lets users configure the prompt for an AI Block.',
	},
	configurationModeLabel: {
		id: 'fabric.editor.agentManagedExtension.config.configurationModeLabel.ai-non-final',
		defaultMessage: 'Customization mode',
		description:
			'Label for the control that lets users switch between structured configuration and manual prompt modes in the AI Block configuration popup.',
	},
	configurationModeConfig: {
		id: 'fabric.editor.agentManagedExtension.config.configurationModeConfig.ai-non-final',
		defaultMessage: 'Configuration',
		description:
			'Radio option label for choosing structured configuration mode in the AI Block configuration popup.',
	},
	configurationModePrompt: {
		id: 'fabric.editor.agentManagedExtension.config.configurationModePrompt.ai-non-final',
		defaultMessage: 'Manual prompt',
		description:
			'Radio option label for choosing manual prompt mode in the AI Block configuration popup.',
	},
	contentGoalLabel: {
		id: 'fabric.editor.agentManagedExtension.config.contentGoalLabel.ai-non-final',
		defaultMessage: 'Content',
		description:
			'Label for the select field where users choose what an AI Block should create from the configured sources.',
	},
	goalExecutiveSummary: {
		id: 'fabric.editor.agentManagedExtension.config.goalExecutiveSummary.ai-non-final',
		defaultMessage: 'Executive summary',
		description:
			'Option label for configuring an AI Block to create an executive summary from selected sources.',
	},
	goalDecisionRegister: {
		id: 'fabric.editor.agentManagedExtension.config.goalDecisionRegister.ai-non-final',
		defaultMessage: 'Decision register',
		description:
			'Option label for configuring an AI Block to create a decision register from selected sources.',
	},
	goalSystemDesign: {
		id: 'fabric.editor.agentManagedExtension.config.goalSystemDesign.ai-non-final',
		defaultMessage: 'System design',
		description:
			'Option label for configuring an AI Block to create a system design summary from selected sources.',
	},
	goalMeetingRecap: {
		id: 'fabric.editor.agentManagedExtension.config.goalMeetingRecap.ai-non-final',
		defaultMessage: 'Meeting recap',
		description:
			'Option label for configuring an AI Block to create a meeting recap from selected sources.',
	},
	outputFormatLabel: {
		id: 'fabric.editor.agentManagedExtension.config.outputFormatLabel.ai-non-final',
		defaultMessage: 'Format',
		description:
			'Label for the tile group where users choose the format of generated AI Block content.',
	},
	outputFormatText: {
		id: 'fabric.editor.agentManagedExtension.config.outputFormatText.ai-non-final',
		defaultMessage: 'Text',
		description: 'Radio option label for generating AI Block content as plain text.',
	},
	outputFormatTextIcon: {
		id: 'fabric.editor.agentManagedExtension.config.outputFormatTextIcon.ai-non-final',
		defaultMessage: 'A',
		description:
			'Decorative letter icon shown for the text output format option in the AI Block configuration popup.',
	},
	outputFormatList: {
		id: 'fabric.editor.agentManagedExtension.config.outputFormatList.ai-non-final',
		defaultMessage: 'List',
		description: 'Radio option label for generating AI Block content as a list.',
	},
	outputFormatTable: {
		id: 'fabric.editor.agentManagedExtension.config.outputFormatTable.ai-non-final',
		defaultMessage: 'Table',
		description: 'Radio option label for generating AI Block content as a table.',
	},
	outputFormatPanel: {
		id: 'fabric.editor.agentManagedExtension.config.outputFormatPanel.ai-non-final',
		defaultMessage: 'Panel',
		description: 'Radio option label for generating AI Block content as a panel.',
	},
	outputFormatDiagram: {
		id: 'fabric.editor.agentManagedExtension.config.outputFormatDiagram.ai-non-final',
		defaultMessage: 'Diagram',
		description:
			'Locked tile option label for generating system design AI Block content as a diagram.',
	},
	sourcesLabel: {
		id: 'fabric.editor.agentManagedExtension.config.sourcesLabel.ai-non-final',
		defaultMessage: 'Sources',
		description:
			'Label for the text area where users add source URLs for an AI Block to use as context.',
	},
	sourcesModeManual: {
		id: 'fabric.editor.agentManagedExtension.config.sourcesModeManual.ai-non-final',
		defaultMessage: 'Provide a list of sources',
		description: 'Option label for manually providing source links for an AI Block.',
	},
	sourcesModeCurrentPage: {
		id: 'fabric.editor.agentManagedExtension.config.sourcesModeCurrentPage.ai-non-final',
		defaultMessage: 'Current page and linked sources',
		description:
			'Option label for using the current Confluence page as the source for an AI Block.',
	},
	sourcesPlaceholder: {
		id: 'fabric.editor.agentManagedExtension.config.sourcesPlaceholder.ai-non-final',
		defaultMessage: 'Paste a link',
		description: 'Placeholder text for the source URL input in the AI Block configuration popup.',
	},
	sourceDisclaimerCopy: {
		id: 'fabric.editor.agentManagedExtension.config.sourceDisclaimerCopy.ai-non-final',
		defaultMessage:
			'Content visibility is based on your permissions. Please verify your source and page security settings to keep data safe.',
		description:
			'Inline information message shown under AI Block source controls to remind users to review source content and page permissions.',
	},
	sourceRequiredError: {
		id: 'fabric.editor.agentManagedExtension.config.sourceRequiredError.ai-non-final',
		defaultMessage: 'Add at least one valid source link.',
		description:
			'Validation message shown when source links are required for an AI Block but none have been added.',
	},
	sourcePendingError: {
		id: 'fabric.editor.agentManagedExtension.config.sourcePendingError.ai-non-final',
		defaultMessage: 'Wait for source links to finish resolving.',
		description:
			'Validation message shown when source links are still resolving and the AI Block cannot be saved yet.',
	},
	sourceInvalidError: {
		id: 'fabric.editor.agentManagedExtension.config.sourceInvalidError.ai-non-final',
		defaultMessage: 'Fix or remove sources that are invalid.',
		description:
			'Validation message shown when one or more source links cannot be resolved and must be fixed before save.',
	},
	sourceLimitError: {
		id: 'fabric.editor.agentManagedExtension.config.sourceLimitError.ai-non-final',
		defaultMessage: 'You can add up to 10 source links.',
		description:
			'Validation message shown when a user attempts to add more than 10 source links to an AI Block.',
	},
	sourceResolving: {
		id: 'fabric.editor.agentManagedExtension.config.sourceResolving.ai-non-final',
		defaultMessage: 'Resolving link',
		description:
			'Accessible status text shown while a pasted source URL is being resolved as a smart link.',
	},
	sourceInvalid: {
		id: 'fabric.editor.agentManagedExtension.config.sourceInvalid.ai-non-final',
		defaultMessage: 'Unable to resolve link',
		description:
			'Inline status text shown when a pasted source URL could not be resolved as a smart link.',
	},
	sourceInvalidUrl: {
		id: 'fabric.editor.agentManagedExtension.config.sourceInvalidUrl.ai-non-final',
		defaultMessage: 'Enter a valid URL.',
		description: 'Inline status text shown when a pasted source value is not a valid URL.',
	},
	sourceNotFound: {
		id: 'fabric.editor.agentManagedExtension.config.sourceNotFound.ai-non-final',
		defaultMessage: 'Not Found',
		description:
			'Inline status text shown when smart link resolution reports that a source URL was not found.',
	},
	sourceUnavailable: {
		id: 'fabric.editor.agentManagedExtension.config.sourceUnavailable.ai-non-final',
		defaultMessage: 'Unavailable',
		description:
			'Inline status text shown when smart link resolution is unavailable for a source URL.',
	},
	retrySourceLabel: {
		id: 'fabric.editor.agentManagedExtension.config.retrySourceLabel.ai-non-final',
		defaultMessage: 'Retry {source}',
		description:
			'Accessible label for a button that retries smart link resolution for a source link.',
	},
	sourceLinksLabel: {
		id: 'fabric.editor.agentManagedExtension.config.sourceLinksLabel.ai-non-final',
		defaultMessage: 'Source links',
		description:
			'Accessible label for the list of source links added to the AI Block configuration popup.',
	},
	removeSourceLabel: {
		id: 'fabric.editor.agentManagedExtension.config.removeSourceLabel.ai-non-final',
		defaultMessage: 'Remove {source}',
		description:
			'Accessible label for a button that removes a source link from the AI Block configuration popup.',
	},
	additionalContextLabel: {
		id: 'fabric.editor.agentManagedExtension.config.additionalContextLabel.ai-non-final',
		defaultMessage: 'Additional Context',
		description:
			'Label for the optional checkbox where users choose whether to provide extra context for an AI Block configuration.',
	},
	additionalContextDetailsLabel: {
		id: 'fabric.editor.agentManagedExtension.config.additionalContextDetailsLabel.ai-non-final',
		defaultMessage: 'Additional Context details',
		description:
			'Accessible label for the text area where users provide extra details after selecting the Additional Context checkbox.',
	},
	additionalContextPlaceholder: {
		id: 'fabric.editor.agentManagedExtension.config.additionalContextPlaceholder.ai-non-final',
		defaultMessage: 'Add details the agent should consider when creating this block.',
		description:
			'Placeholder text in the optional Additional Context text area for an AI Block configuration.',
	},
	additionalContextRequiredError: {
		id: 'fabric.editor.agentManagedExtension.config.additionalContextRequiredError.ai-non-final',
		defaultMessage: 'Provide additional context or uncheck this option.',
		description:
			'Validation message shown when the Additional Context checkbox is selected but its text area does not contain usable text.',
	},
	additionalContextCharactersRemaining: {
		id: 'fabric.editor.agentManagedExtension.config.additionalContextCharactersRemaining.ai-non-final',
		defaultMessage: '{remaining} characters left',
		description:
			'Character counter shown below the Additional Context text area. The remaining placeholder is the number of characters the user can still enter.',
	},
	promptToggleLabel: {
		id: 'fabric.editor.agentManagedExtension.config.promptToggleLabel.ai-non-final',
		defaultMessage: 'Describe what you want',
		description:
			'Button label for expanding the manual prompt section in the AI Block configuration popup.',
	},
	updateIntervalLabel: {
		id: 'fabric.editor.agentManagedExtension.config.updateIntervalLabel.ai-non-final',
		defaultMessage: 'Preferred update interval',
		description:
			'Label for the select field where users choose an opaque preferred update interval for an AI Block.',
	},
	updateIntervalShort: {
		id: 'fabric.editor.agentManagedExtension.config.updateIntervalShort.ai-non-final',
		defaultMessage: 'Short',
		description:
			'Option label for the shortest preferred update interval for an AI Block without promising exact timing.',
	},
	updateIntervalMedium: {
		id: 'fabric.editor.agentManagedExtension.config.updateIntervalMedium.ai-non-final',
		defaultMessage: 'Medium',
		description:
			'Option label for the medium preferred update interval for an AI Block without promising exact timing.',
	},
	updateIntervalLong: {
		id: 'fabric.editor.agentManagedExtension.config.updateIntervalLong.ai-non-final',
		defaultMessage: 'Long',
		description:
			'Option label for the longest preferred update interval for an AI Block without promising exact timing.',
	},
});
